// campaigns-routes.tsx — the canonical campaign route table (SSoT), shared by
// BOTH routers so they can never drift:
//   • the website's history router (real URLs) — see website.tsx
//   • the extension's in-memory router (shadow-DOM overlay on web.whatsapp.com,
//     whose page URL belongs to WhatsApp and must not be touched) — see
//     blueticks.tsx, which wraps <CampaignsRoutes/> in a <MemoryRouter>.
//
// Every path renders the same route-driven shell (CampaignsRedesignView); the
// shell derives its surface + step from the params/path. Order matters: the
// literal `/new` and the `/:id/edit|launched|…` children MUST precede the bare
// `/:id` detail route so they aren't shadowed by it.

import React from 'react';
import { createMemoryRouter, RouterProvider } from 'react-router-dom';

import CampaignsRedesignView from './campaigns-redesign-view';

/** Canonical `/campaigns/*` paths, ordered so specific routes precede `/:id`. */
export const CAMPAIGN_REDESIGN_PATHS = [
  '/campaigns',
  '/campaigns/new',
  '/campaigns/:id/edit/:step',
  '/campaigns/:id/launched',
  '/campaigns/audiences/:boardId/edit',
  '/campaigns/templates/new',
  '/campaigns/templates/:templateId/edit',
  '/campaigns/:id/logs',
  '/campaigns/:id',
] as const;

/**
 * The same table under the white-labelled flow's prefix. Derived, never
 * hand-written: a path added to CAMPAIGN_REDESIGN_PATHS is automatically routed
 * in both products, so the clear flow cannot silently lose a campaign surface.
 * `/clear-campaigns` itself is ALSO written literally in website.tsx — the
 * route/MIRRORED_ROOTS sync test scans that file for the literal.
 */
export const CLEAR_CAMPAIGN_REDESIGN_PATHS = CAMPAIGN_REDESIGN_PATHS.map(
  (path) => path.replace(/^\/campaigns/, '/clear-campaigns'),
);

// ── Extension router ────────────────────────────────────────────────────────
// The extension overlay is a shadow-DOM injection on web.whatsapp.com whose page
// URL belongs to WhatsApp, so the redesign routes in memory.
//
// We use the DATA router (createMemoryRouter + RouterProvider), NOT the declarative
// <MemoryRouter>/<HistoryRouter>. Why: inside the InjectionService's shadow-DOM
// createRoot, the declarative routers did not re-render on navigation — the history
// updated and its listeners fired, but the Router's useState-in-history.listen never
// repainted (path navigations silently no-op'd; only ?tab= query changes survived).
// react-redux updates DO repaint in this same root, and react-redux v8 +
// RouterProvider both subscribe via useSyncExternalStore — so the data router
// repaints reliably where the useState-based declarative router didn't. The router
// is a STABLE module-level singleton, so it also survives the overlay's re-renders /
// open-close remounts (a fresh MemoryRouter would reset to '/campaigns'); reopening
// resumes where you left off.
export const campaignsMemoryRouter = createMemoryRouter(
  CAMPAIGN_REDESIGN_PATHS.map((path) => ({ path, element: <CampaignsRedesignView /> })),
  { initialEntries: ['/campaigns'] },
);

/** The redesign mounted on the stable in-memory data router — the extension's entry. */
export const ExtensionCampaignsRouter: React.FC = () => (
  <RouterProvider router={campaignsMemoryRouter} />
);
